import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react"
import { Button } from "react-bootstrap"
import { FormattedMessage } from "react-intl"

import PageLoadingPanel from "../../../shared_components/widgets/PageLoadingPanel"
import PageErrorPanel from "../../../shared_components/widgets/PageErrorPanel"
import { LanzaTable, UniversalFilter, onBooleanSort } from "../../../shared_components/widgets/LanzaTable"
import { API_STATUS } from "../../../shared_components/widgets/utils"
import { LocaleFormattedDate, LocaleFormattedUser } from "../../internationalization/LocaleFormatted"
import { logToServer } from "../../../ErrorBoundary"
import { addNewObject, getObjectsList, updateObject } from "../../../Loaders"
import { hideModal, showModal, stringifyGroupName } from "../../../shared_components/utils"
import { UserModal } from "./UserModal"
import { PasswordModal } from "./PasswordModal"
import { PageTitle } from "../../../config/AppSettingsConfigProvider"
import { withAppSettingsConfig } from "../../../config/AppSettingsConfigProvider"

const UsersTable = forwardRef((props, ref) => {
  const { intl, config, allUsers: users, reloadUsers, userStatus } = props
  const [isUsingCognito, setIsUsingCognito] = useState(false)
  useEffect(() => {
    if (config?.settings) {
      setIsUsingCognito(config?.settings?.USE_SSO)
    }
  }, [config])
  const [selectedUser, setSelectedUser] = useState(null)
  const [groups, setGroups] = useState([])
  const [interfaces, setInterfaces] = useState([])
  const [enterUserModal, setEnterUserModal] = useState(false)
  const [enterPasswordModal, setEnterPasswordModal] = useState(false)
  const [isAddingNewUser, setIsAddingNewUser] = useState(false)
  const getGroups = () => {
    getObjectsList("/users/group")
      .then((groups) => {
        setGroups(groups)
      })
      .catch((error) => {
        logToServer(error)
      })
  }
  const getGraphInterfaces = () => {
    getObjectsList("/users/graph-interfaces")
      .then((interfaces) => {
        setInterfaces(interfaces)
      })
      .catch((error) => {
        logToServer(error)
      })
  }

  // packages up the group names into an aray of strings
  const transformUser = (usr, vals) => {
    // Remove any fields from the user that we don't edit on the frontend
    let unusedFields = ["last_login", "date_joined"]

    unusedFields.map((field) => {
      delete usr[field]
    })

    // Replace user values with those entered into the edit form
    let keys = Object.keys(vals)
    keys.forEach((key) => {
      usr[key] = vals[key]
    })

    // Rework the boolean group names into a list. Also, remove the boolean fields
    let group_names = []

    groups.map((grp) => {
      let group_name = stringifyGroupName(grp)
      if (vals[group_name]) {
        group_names.push(grp.name)
      }
      // remove the boolean version of the group name from the user
      delete usr[group_name]
    })

    usr.group_names = group_names

    return usr
  }

  const onHideModal = (setModalFunction) => {
    hideModal(setModalFunction)
    setSelectedUser(null)
  }

  const newUser = {
    username: "",
    email: "",
    first_name: "",
    last_name: "",
    is_active: true,
    is_staff: false,
    is_superuser: false,
    is_system_user: false,
    is_file_uploader: false,
    is_graph_config_editor: false,
    user_graph_interfaces: [],
  }
  const submitUserData = (user, values) => {
    user = transformUser(selectedUser, values)

    // If we don't have a user id, then we need to create a new user.
    let saveUrl = ""
    const addOrUpdateObject = user?.id ? addNewObject : updateObject
    {
      saveUrl = user?.id ? `/users/list/${user.id}/save/` : `/users/list/create_new_user/`
    }
    addOrUpdateObject(saveUrl, user, true)
      .then((_) => {
        reloadUsers()
      })
      .catch((error) => {
        let errorMessage = "\n"
        console.error(error)
        logToServer(error)
        error
          .json()
          .then((data) => {
            console.error(data)
            logToServer(data)
            errorMessage = errorMessage + data.message
            triggerAlert("samplemanagement.error.unable.to.save.data", errorMessage)
          })
          .catch((err) => {
            console.error(err)
            logToServer(err)
            errorMessage = errorMessage + error.statusText
            triggerAlert("samplemanagement.error.unable.to.save.data", errorMessage)
          })
      })
  }

  const triggerAlert = (id, err = "") => {
    alert(intl.formatMessage({ id: id }) + ":\n" + err)
  }

  useEffect(() => {
    getGroups()
    getGraphInterfaces()
  }, [])

  useImperativeHandle(ref, () => ({
    refresh() {
      reloadUsers()
    },
  }))

  const IconIndicator = ({ value }) =>
    value ? (
      <span className={"fa fa-check-circle text-success"} />
    ) : (
      <span className={"fa fa-minus-circle text-danger"} />
    )

  const TableColumnFiter = ({ filterProps = {}, filterType = "boolean", customOptions }) => {
    const { column, preFilteredRows } = filterProps
    return (
      <UniversalFilter
        column={column}
        setFilter={column.setFilter}
        preFilteredRows={preFilteredRows}
        filterType={filterType}
        isClearable={false}
        customOptions={customOptions}
      />
    )
  }

  return (
    <div className="content-block full-height-min">
      <PageTitle glyph={"user"} id={"navigation.userManagementOption"} description={"User Management"} />
      <div style={{ background: "var(--lanzalake-orange)", padding: "5px" }} className={"solid-grey-border"}>
        Note: &nbsp;
        {isUsingCognito ? (
          <>
            User management is also required on AWS Cognito. UI users must exist both on Cognito and on Django
            with exact matching usernames. For UI users, the password being checked is the Cognito one [a
            Django password is not used or required for these users]. For the api-token users, such as data
            loggers and other service accounts, the password being used is the Django one [service users DONT
            require a Cognito entry].
          </>
        ) : (
          <>User management is fully managed by Django - No external action required.</>
        )}
      </div>
      <hr />
      {userStatus === API_STATUS.LOADING && <PageLoadingPanel />}

      {userStatus === API_STATUS.ERROR && <PageErrorPanel attemptReload={reloadUsers} />}

      {userStatus === API_STATUS.SUCCESS &&
        (users.length ? (
          <LanzaTable
            className={"table table-bordered table-hover tbl_lanzatable"}
            columns={[
              {
                Header: <FormattedMessage id={"administration.user-management.username"} />,
                accessor: "username",
              },
              {
                Header: <FormattedMessage id={"general.name"} />,
                id: "name",
                style: { minWidth: "75px" },
                accessor: (row) => `${row.first_name} ${row.last_name} ${row.username}`, // Actual text to be filtered
                Cell: (
                  { row: { original } }, // Display using LocaleFormattedUser - can't filter on this
                ) => <LocaleFormattedUser user={original} />,
              },
              {
                Header: <FormattedMessage id={"general.email"} />,
                accessor: "email",
              },
              {
                Header: <FormattedMessage id={"administration.user-management.roles"} />,
                accessor: "group_names",
                Cell: ({ value }) => value.join(", "),
                Filter: (filterProps) => {
                  const {
                    column: { preFilteredRows, id },
                  } = filterProps
                  const options = React.useMemo(() => {
                    const options = new Set()
                    preFilteredRows.forEach((row) => {
                      let groups = row.values[id]
                      groups.forEach((val) => {
                        options.add(val)
                      })
                    })
                    return [...options.values()]
                  }, [id, preFilteredRows])

                  // Render a multi-select box
                  return (
                    <TableColumnFiter
                      filterProps={filterProps}
                      filterType={"select"}
                      customOptions={options.map((o, i) => ({
                        value: o,
                        text: o,
                      }))}
                    />
                  )
                },
              },
              {
                Header: <FormattedMessage id={"administration.user-management.can_upload_file"} />,
                accessor: "is_file_uploader",
                sortType: onBooleanSort,
                Filter: (filterProps) => <TableColumnFiter filterProps={filterProps} />,
                Cell: ({ value }) => <IconIndicator value={value} />,
                style: {
                  textAlign: "center",
                  maxWidth: "100px",
                },
              },
              {
                Header: (
                  <FormattedMessage
                    id={"administration.user-management.can_edit_graph_configs"}
                    defaultMessage={"Can Edit Default Graph Configs"}
                  />
                ),
                accessor: "is_graph_config_editor",
                sortType: onBooleanSort,
                Filter: (filterProps) => <TableColumnFiter filterProps={filterProps} />,
                Cell: ({ value }) => <IconIndicator value={value} />,
                style: {
                  textAlign: "center",
                  maxWidth: "100px",
                },
              },
              {
                Header: <FormattedMessage id={"general.active"} />,
                accessor: "is_active",
                sortType: onBooleanSort,
                Filter: (filterProps) => <TableColumnFiter filterProps={filterProps} />,
                Cell: ({ value }) => <IconIndicator value={value} />,
                style: {
                  textAlign: "center",
                },
              },
              {
                Header: <FormattedMessage id={"administration.user-management.last_login"} />,
                accessor: "last_login",
                Cell: ({ value }) => (value ? <LocaleFormattedDate date={value} /> : ""),
              },
              {
                Header: <FormattedMessage id={"administration.user-management.edit.user"} />,
                id: "edit",
                className: "text-center",
                accessor: (user) => {
                  return (
                    <Button
                      variant={"outline-primary"}
                      className={"btn btn-sm"}
                      title={intl.formatMessage({ id: "general.enter.data" })}
                      onClick={(event) => {
                        event.preventDefault()
                        const userGroupProps = []
                        let value
                        groups.map((grp) => {
                          value = user?.group_names?.includes(grp)
                          userGroupProps[stringifyGroupName(grp)] = value
                        })
                        setSelectedUser({
                          ...user,
                          ...userGroupProps,
                        })
                        setIsAddingNewUser(() => false)
                        showModal(setEnterUserModal)
                      }}
                    >
                      <span className={"fa fa-pencil-square-o"} />
                      &nbsp;
                      {intl.formatMessage({ id: "general.edit" })}
                    </Button>
                  )
                },
                disableSortBy: true,
                disableFilters: true,
              },
              {
                Header: <FormattedMessage id={"administration.user-management.change-password"} />,
                id: "password",
                className: "text-center",
                accessor: (user) => {
                  return (
                    <Button
                      variant={"outline-primary"}
                      className={"btn btn-sm"}
                      title={intl.formatMessage({ id: "general.enter.data" })}
                      onClick={(event) => {
                        event.preventDefault()
                        setSelectedUser(user)
                        showModal(setEnterPasswordModal)
                      }}
                    >
                      <span className={"fa fa-key"} />
                      &nbsp;
                      {intl.formatMessage({ id: "general.change" })}
                    </Button>
                  )
                },
                disableSortBy: true,
                disableFilters: true,
              },
            ]}
            data={users}
            allowSorting={true}
            allowFilters={true}
            allowGlobalFilter={false}
            allowRowSelection={false}
            initialTableOptions={{
              sortBy: [{ id: "username", desc: false }],
              pageSize: 10,
            }}
          />
        ) : (
          <div className={"center_middle"} style={{ height: "200px" }}>
            <div align={"center"} />
          </div>
        ))}
      <div style={{ marginTop: "20px" }}>
        <Button
          variant={"outline-primary"}
          onClick={(e) => {
            e.preventDefault()
            setSelectedUser(newUser)
            setIsAddingNewUser(() => true)
            showModal(setEnterUserModal)
          }}
        >
          <span className={`fa fa-plus-circle`} />
          &nbsp;&nbsp;
          {intl.formatMessage({ id: "general.create.new" })}
        </Button>
      </div>

      {/*---------- Modals ----------*/}
      {selectedUser && enterUserModal && (
        <>
          <UserModal
            enterUserModal={enterUserModal}
            groups={groups}
            interfaces={interfaces}
            intl={intl}
            onHide={() => onHideModal(setEnterUserModal)}
            selectedUser={selectedUser}
            submitUserData={submitUserData}
            isAddingNewUser={isAddingNewUser}
          />
        </>
      )}

      {selectedUser && enterPasswordModal && (
        <>
          <PasswordModal
            triggerAlert={triggerAlert}
            getUsers={reloadUsers}
            intl={intl}
            onHide={() => onHideModal(setEnterPasswordModal)}
            show={enterPasswordModal}
            user={selectedUser}
          />
        </>
      )}
      {/*---------- /Modals ----------*/}
    </div>
  )
})

export default withAppSettingsConfig(UsersTable)
